// src/components/DynamicGenerationDemo.jsx
// Complete demonstration of dynamic design generation system

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Flex, Heading, Text, Button, VStack, HStack, 
  Badge, Alert, AlertIcon, Tabs, TabList, TabPanels, Tab, TabPanel,
  Code, SimpleGrid, Input, Select, Switch, FormControl, FormLabel,
  Textarea, Divider
} from '@chakra-ui/react';
import DynamicDesignGenerator from './DynamicDesignGenerator';
import { normalizeEventTags, analyzeImageContentWithEventTags } from '../utils/contentAnalysis';
// Background library now loads directly from Supabase - no manual update needed
import { debugFeatureFlags, getEnabledFeatures } from '../utils/featureFlags';
import { mapEventDashboardToEventTags, createPhotoContext } from '../utils/eventTagMapping';
import { loadBackgroundsFromSupabase } from '../utils/backgroundLibrary';

/**
 * Complete Demo of Dynamic Design Generation System
 * Shows: Event Tags + Background Library + AI Analysis + CESDK Generation
 */
const DynamicGenerationDemo = ({ user }) => {
  const navigate = useNavigate();
  
  // Make navigate available globally for the navigation function
  useEffect(() => {
    window.reactNavigate = navigate;
    return () => {
      delete window.reactNavigate;
    };
  }, [navigate]);

  // Test state
  const [selectedImage, setSelectedImage] = useState('');
  const [eventTags, setEventTags] = useState({});
  const [customEventTags, setCustomEventTags] = useState('');
  const [showGenerator, setShowGenerator] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backgroundsLoaded, setBackgroundsLoaded] = useState(null);
  const [eventTagExample, setEventTagExample] = useState(null);

  // Real examples matching your EventDashboard.jsx structure
  const mockEventData = {
    id: 'event-123',
    name: 'Spring Championship Tournament',
    event_type: 'baseball',
    city: 'Atlanta',
    state: 'GA',
    start_date: '2024-04-15',
    end_date: '2024-04-17',
    description: 'Annual spring baseball championship'
  };

  const mockUserData = {
    id: 'user-456',
    first_name: 'John',
    last_name: 'Doe',
    email: 'john.doe@example.com',
    phone: '+1-555-123-4567',
    order_status: 'completed',
    order_created_at: '2024-03-01T10:30:00Z',
    answers: {
      athlete_first_name: 'Mike',
      athlete_last_name: 'Johnson',
      team_name: 'Eagles',
      position: 'Pitcher',
      player_number: '15',
      team_colors: ['#003366', '#FFD700'],
      coach_name: 'Coach Smith',
      league: 'Little League',
      achievements: ['MVP', 'All-Star']
    }
  };

  // Sample test images
  const testImages = [
    {
      name: 'Baseball Game Day Style',
      url: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=800',
      eventData: {
        ...mockEventData,
        event_type: 'championship'
      },
      userData: {
        ...mockUserData,
        answers: {
          ...mockUserData.answers,
          opponent: 'Indians'
        }
      },
      description: 'Will generate "Game Day" announcement style like your first example'
    },
    {
      name: 'Cheerleader Color Dust Style',
      url: 'https://images.unsplash.com/photo-1594736797933-d0c2d737b9e6?w=800',
      eventData: {
        ...mockEventData,
        name: 'The Cheer Pitt',
        event_type: 'cheerleading'
      },
      userData: {
        ...mockUserData,
        answers: {
          ...mockUserData.answers,
          athlete_first_name: 'Scarlett',
          athlete_last_name: 'Baker',
          team_name: 'Summit',
          organization: 'The Cheer Pitt',
          level: 'Summit',
          season: '2025'
        }
      },
      description: 'Will generate color dust portrait like your second example'
    },
    {
      name: 'Media Day Portrait',
      url: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800',
      eventData: {
        ...mockEventData,
        name: 'Media Day Photos',
        event_type: 'media_day'
      },
      userData: {
        ...mockUserData,
        answers: {
          ...mockUserData.answers,
          athlete_first_name: 'Alex',
          athlete_last_name: 'Rodriguez',
          team_name: 'Warriors',
          position: 'Center',
          player_number: '32',
          team_colors: ['#1E3A8A', '#F59E0B'],
          organization: 'Elite Sports Academy'
        }
      },
      description: 'Professional media day style for team photos and player portraits'
    },
    {
      name: 'Basketball Action Shot',
      url: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800',
      eventData: {
        ...mockEventData,
        name: 'Basketball Tournament',
        event_type: 'basketball'
      },
      userData: {
        ...mockUserData,
        answers: {
          ...mockUserData.answers,
          athlete_first_name: 'Sarah',
          athlete_last_name: 'Williams',
          team_name: 'Tigers',
          position: 'Point Guard',
          player_number: '23',
          team_colors: ['#FF4500', '#000000']
        }
      },
      description: 'Standard player card layout'
    }
  ];

  // Sample event tag presets
  const eventTagPresets = {
    'Championship Game': {
      event_type: 'championship',
      season_phase: 'playoffs',
      mood: 'triumphant',
      design_intensity: 'bold',
      include_stats: true,
      focus: 'achievement'
    },
    'Team Photo Day': {
      event_type: 'team_photo',
      photo_type: 'team',
      mood: 'collaborative',
      design_intensity: 'medium',
      focus: 'team_unity',
      background_preference: 'clean'
    },
    'Player Spotlight': {
      event_type: 'individual_showcase',
      photo_type: 'headshot',
      focus: 'stats',
      include_stats: true,
      mood: 'professional',
      design_intensity: 'medium'
    },
    'Action Highlights': {
      event_type: 'game_action',
      photo_type: 'action',
      mood: 'dynamic',
      design_intensity: 'bold',
      focus: 'performance',
      background_preference: 'energetic'
    }
  };

  // Handle image selection
  const handleImageSelect = (image) => {
    setSelectedImage(image.url);
    
    // Generate real event tags using your EventDashboard mapping
    const generatedEventTags = mapEventDashboardToEventTags(
      image.eventData, 
      image.userData
    );
    
    setEventTags(generatedEventTags);
    setEventTagExample(generatedEventTags);
    setShowGenerator(false);
  };

  // Handle custom event tags
  const handleCustomEventTags = () => {
    try {
      const parsed = JSON.parse(customEventTags);
      setEventTags(parsed);
    } catch (error) {
      alert('Invalid JSON format for event tags');
    }
  };

  // Test enhanced analysis
  const testEnhancedAnalysis = async () => {
    if (!selectedImage) {
      alert('Please select an image first');
      return;
    }

    setLoading(true);
    try {
      const normalizedTags = normalizeEventTags(eventTags);
      const result = await analyzeImageContentWithEventTags(selectedImage, normalizedTags);
      setAnalysisResult(result);
      console.log('🧪 Enhanced Analysis Result:', result);
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Background database now loads automatically from Supabase
  // No manual initialization needed - backgrounds are fetched from your real storage

  // Get enabled features
  const enabledFeatures = getEnabledFeatures(user?.email);

  // Test real Supabase background loading
  const testBackgroundLoading = async () => {
    try {
      const { allBackgrounds, backgroundsBySport } = await loadBackgroundsFromSupabase();
      setBackgroundsLoaded({
        total: allBackgrounds.length,
        sports: Object.keys(backgroundsBySport),
        sampleBackgrounds: allBackgrounds.slice(0, 3)
      });
    } catch (error) {
      console.error('Error loading backgrounds:', error);
      setBackgroundsLoaded({ error: error.message });
    }
  };

  // Navigate to dynamic generation with proper state
  const handleNavigateToGeneration = () => {
    if (!selectedImage || !eventTags) {
      alert('Please select an image and ensure event tags are loaded');
      return;
    }

    // Create a unique ID for this dynamic generation session
    const dynamicSessionId = `dynamic-${Date.now()}`;
    
    const generationData = {
      sessionId: dynamicSessionId,
      uploadedImageURL: selectedImage,
      eventTags: eventTags,
      generationType: 'dynamic',
      timestamp: Date.now()
    };
    
    // Store the generation data in sessionStorage with error handling
    try {
      sessionStorage.setItem('dynamicGenerationData', JSON.stringify(generationData));
      // Also store in localStorage as backup
      localStorage.setItem('dynamicGenerationData', JSON.stringify(generationData));
      console.log('🔄 Stored generation data:', generationData);
    } catch (error) {
      console.error('Failed to store generation data:', error);
      alert('Failed to prepare generation data. Please try again.');
      return;
    }
    
    // Use React Router navigate instead of window.location.href
    // First ensure we're in the test page where useNavigate is available
    if (window.location.pathname === '/test-dynamic-generation') {
      // Navigate using React Router if available, otherwise fallback
      try {
        const navigate = window.reactNavigate;
        if (navigate) {
          navigate(`/dynamic-editor/${dynamicSessionId}`, { 
            state: { generationData },
            replace: false 
          });
        } else {
          throw new Error('React Router navigate not available');
        }
      } catch (error) {
        console.log('Using fallback navigation method...');
        // Fallback to window.location with delay to ensure storage
        setTimeout(() => {
          window.location.href = `/dynamic-editor/${dynamicSessionId}`;
        }, 100);
      }
    } else {
      // Direct navigation fallback
      setTimeout(() => {
        window.location.href = `/dynamic-editor/${dynamicSessionId}`;
      }, 100);
    }
  };

  // Check if user was redirected from old customize/dynamic route
  const wasRedirectedFromOldRoute = sessionStorage.getItem('dynamicRedirectMessage');
  if (wasRedirectedFromOldRoute) {
    sessionStorage.removeItem('dynamicRedirectMessage');
  }

  return (
    <Box p={6} maxW="7xl" mx="auto">
      {/* Redirect Notice */}
      {wasRedirectedFromOldRoute && (
        <Alert status="info" mb={6} borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">🔄 Welcome to Dynamic Generation!</Text>
            <Text fontSize="sm">
              You were redirected here because the dynamic generation system has moved to this new interface. 
              Select an image below to get started with AI-powered design generation.
            </Text>
          </Box>
        </Alert>
      )}

      {/* Header */}
      <VStack spacing={4} textAlign="center" mb={8}>
        <Badge colorScheme="purple" variant="solid" fontSize="md" p={2}>
          🧪 REAL DATA INTEGRATION DEMO
        </Badge>
        <Heading size="xl" color="gray.800">
          AI-Powered Sports Design System
        </Heading>
        <Text color="gray.600" maxW="2xl">
          Test dynamic design generation using your Supabase backgrounds and EventDashboard event tag mapping.
        </Text>
      </VStack>

      {/* Feature Status */}
      <Alert status="success" mb={6} borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">🚀 System Status: ALL FEATURES ENABLED</Text>
          <Text fontSize="sm">
            Dynamic Generation • Background Library • Event Tag Analysis • Real-time Composition
          </Text>
        </Box>
      </Alert>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>🎯 Test Dynamic Generation</Tab>
          <Tab>🌄 Test Background Loading</Tab>
          <Tab>🏷️ Event Tag Mapping</Tab>
          <Tab>📖 Integration Guide</Tab>
        </TabList>

        <TabPanels>
          {/* Dynamic Generation Test Tab */}
          <TabPanel>
            <VStack spacing={6}>
              <Box>
                <Heading size="md" mb={4}>Select Test Image with Real Event Data</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {testImages.map((image, idx) => (
                    <Box
                      key={idx}
                      p={4}
                      border="2px"
                      borderColor={selectedImage === image.url ? "blue.500" : "gray.200"}
                      borderRadius="lg"
                      cursor="pointer"
                      onClick={() => handleImageSelect(image)}
                    >
                      <img 
                        src={image.url} 
                        alt={image.name}
                        style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                      <Text fontWeight="bold" mt={2}>{image.name}</Text>
                      <Text fontSize="xs" color="gray.600" mt={1}>{image.description}</Text>
                      <HStack spacing={2} mt={2}>
                        <Badge colorScheme="blue">{image.eventData.event_type}</Badge>
                        <Badge colorScheme="green">{image.userData.answers.team_name}</Badge>
                        <Badge colorScheme="purple">{image.userData.answers.athlete_first_name}</Badge>
                      </HStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>

              {selectedImage && eventTags && (
                <Box p={4} bg="blue.50" borderRadius="lg" w="100%">
                  <Text><strong>Generated Event Tags:</strong></Text>
                  <Text fontSize="sm">
                    Sport: {eventTags.sport} • Player: {eventTags.player_name} • 
                    Team: {eventTags.team_name} • Level: {eventTags.level} • 
                    Event: {eventTags.event_type}
                  </Text>
                </Box>
              )}

              <VStack spacing={4}>
                <HStack spacing={4}>
                  <Button
                    colorScheme="blue"
                    size="lg"
                    onClick={() => setShowGenerator(true)}
                    isDisabled={!selectedImage}
                    leftIcon={<Text>🎨</Text>}
                  >
                    Preview Generation
                  </Button>
                  
                  <Button
                    colorScheme="purple"
                    size="lg"
                    onClick={() => handleNavigateToGeneration()}
                    isDisabled={!selectedImage}
                    leftIcon={<Text>🚀</Text>}
                  >
                    Open in CESDK Editor
                  </Button>
                </HStack>
                
                <Text fontSize="sm" color="gray.600" textAlign="center" maxW="md">
                  <strong>Preview:</strong> See generation results inline • <strong>CESDK Editor:</strong> Full editing capabilities with background removal
                </Text>
              </VStack>

              {showGenerator && selectedImage && (
                <Box w="100%" p={6} bg="white" borderRadius="xl" boxShadow="lg">
                  <DynamicDesignGenerator
                    uploadedImageURL={selectedImage}
                    eventTags={eventTags}
                    user={user}
                  />
                </Box>
              )}
            </VStack>
          </TabPanel>

          {/* Background Loading Test Tab */}
          <TabPanel>
            <VStack spacing={6}>
              <Heading size="md">Test Real Supabase Background Loading</Heading>
              
              <Text color="gray.600" textAlign="center">
                This will load backgrounds from your actual Supabase bucket:<br/>
                <Code>playmakerpics-images/backgrounds/[sport-folders]</Code>
              </Text>

              <Button
                colorScheme="green"
                onClick={testBackgroundLoading}
                leftIcon={<Text>🌄</Text>}
              >
                Load Real Backgrounds from Supabase
              </Button>

              {backgroundsLoaded && (
                <Box p={4} bg="gray.50" borderRadius="lg" w="100%">
                  {backgroundsLoaded.error ? (
                    <Alert status="error">
                      <AlertIcon />
                      Error: {backgroundsLoaded.error}
                    </Alert>
                  ) : (
                    <VStack align="stretch" spacing={3}>
                      <Text fontWeight="bold">✅ Backgrounds Loaded Successfully!</Text>
                      <Text>Total backgrounds: {backgroundsLoaded.total}</Text>
                      <Text>Sports detected: {backgroundsLoaded.sports?.join(', ')}</Text>
                      
                      {backgroundsLoaded.sampleBackgrounds && (
                        <Box>
                          <Text fontWeight="bold" mb={2}>Sample backgrounds:</Text>
                          {backgroundsLoaded.sampleBackgrounds.map((bg, idx) => (
                            <Text key={idx} fontSize="sm">
                              • {bg.folder} - {bg.filename} ({bg.sport})
                            </Text>
                          ))}
                        </Box>
                      )}
                    </VStack>
                  )}
                </Box>
              )}
            </VStack>
          </TabPanel>

          {/* Event Tag Mapping Tab */}
          <TabPanel>
            <VStack spacing={6}>
              <Heading size="md">EventDashboard Data → Event Tags Mapping</Heading>
              
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} w="100%">
                <Box p={4} bg="blue.50" borderRadius="lg">
                  <Heading size="sm" mb={3}>Your EventDashboard Structure</Heading>
                  <Code p={3} borderRadius="md" fontSize="xs" whiteSpace="pre-wrap" w="100%">
{`// Event Data
{
  id: '${mockEventData.id}',
  name: '${mockEventData.name}',
  event_type: '${mockEventData.event_type}',
  city: '${mockEventData.city}',
  state: '${mockEventData.state}'
}

// User Data  
{
  first_name: '${mockUserData.first_name}',
  last_name: '${mockUserData.last_name}',
  answers: {
    athlete_first_name: '${mockUserData.answers.athlete_first_name}',
    athlete_last_name: '${mockUserData.answers.athlete_last_name}',
    team_name: '${mockUserData.answers.team_name}',
    position: '${mockUserData.answers.position}',
    player_number: '${mockUserData.answers.player_number}'
  }
}`}
                  </Code>
                </Box>

                <Box p={4} bg="green.50" borderRadius="lg">
                  <Heading size="sm" mb={3}>Generated Event Tags</Heading>
                  {eventTagExample ? (
                    <Code p={3} borderRadius="md" fontSize="xs" whiteSpace="pre-wrap" w="100%">
                      {JSON.stringify(eventTagExample, null, 2)}
                    </Code>
                  ) : (
                    <Text fontSize="sm" color="gray.600">
                      Select an image above to see generated event tags
                    </Text>
                  )}
                </Box>
              </SimpleGrid>

              <Box p={4} bg="yellow.50" borderRadius="lg" w="100%">
                <Heading size="sm" mb={2}>🔄 Mapping Function Usage</Heading>
                <Code p={3} borderRadius="md" fontSize="xs" whiteSpace="pre-wrap" w="100%">
{`import { mapEventDashboardToEventTags } from '../utils/eventTagMapping';

// Convert your EventDashboard data to event tags
const eventTags = mapEventDashboardToEventTags(
  eventData,    // From your events table
  userData      // From your event_orders with answers
);

// Use with dynamic design generator
<DynamicDesignGenerator
  uploadedImageURL={imageUrl}
  eventTags={eventTags}
  user={user}
/>`}
                </Code>
              </Box>
            </VStack>
          </TabPanel>

          {/* Integration Guide Tab */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Heading size="md">🔗 How to Integrate with Your System</Heading>
              
              <Box p={4} bg="green.50" borderRadius="lg">
                <Heading size="sm" mb={3}>✅ What's Already Connected</Heading>
                <VStack align="start" spacing={2} fontSize="sm">
                  <Text>• Background library connects to your Supabase bucket structure</Text>
                  <Text>• Event tag mapping understands your EventDashboard data format</Text>
                  <Text>• Dynamic scene generator uses CESDK for real-time composition</Text>
                  <Text>• All features are enabled and ready for production use</Text>
                </VStack>
              </Box>

              <Box p={4} bg="blue.50" borderRadius="lg">
                <Heading size="sm" mb={3}>🚀 Integration Steps</Heading>
                <VStack align="start" spacing={2} fontSize="sm">
                  <Text><strong>1. EventDashboard Integration:</strong></Text>
                  <Code fontSize="xs" p={2} borderRadius="md" w="100%">
{`// In your EventDashboard.jsx
import { mapEventDashboardToEventTags } from '../utils/eventTagMapping';
import DynamicDesignGenerator from './DynamicDesignGenerator';

// For each user, generate dynamic designs
const handleGenerateDesigns = (user) => {
  const eventTags = mapEventDashboardToEventTags(event, user);
  
  // Navigate to generation page or show modal
  navigate('/generate-designs', { 
    state: { 
      eventTags, 
      imageUrl: userUploadedImage 
    } 
  });
};`}
                  </Code>
                  
                  <Text><strong>2. Bulk Generation for All Users:</strong></Text>
                  <Code fontSize="xs" p={2} borderRadius="md" w="100%">
{`// Generate designs for all event participants
const generateForAllUsers = () => {
  users.forEach(user => {
    const eventTags = mapEventDashboardToEventTags(event, user);
    // Queue for batch processing or process individually
  });
};`}
                  </Code>

                  <Text><strong>3. Background Library is Auto-Connected:</strong></Text>
                  <Text>• Reads from your existing Supabase bucket structure</Text>
                  <Text>• Maps folder names to sports automatically</Text>
                  <Text>• Scores backgrounds based on event context</Text>
                </VStack>
              </Box>

              <Box p={4} bg="purple.50" borderRadius="lg">
                <Heading size="sm" mb={3}>🎯 Next Steps</Heading>
                <VStack align="start" spacing={1} fontSize="sm">
                  <Text>• Test with your real event data using this demo</Text>
                  <Text>• Add dynamic generation buttons to your EventDashboard</Text>
                  <Text>• Set up batch processing for large events</Text>
                  <Text>• Customize layouts and backgrounds per sport</Text>
                </VStack>
              </Box>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default DynamicGenerationDemo; 